<script setup lang="ts">
  import type { Table } from '@tanstack/vue-table';
  import { computed } from 'vue';

  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Separator } from '@ui/components/separator';
  import TableSelection from '@ui/components/table/TableSelection.vue';
  import { cn } from '@ui/utils/utils';

  interface DataTablePaginationProps {
    fontSize?: string;
    globalSelection?: any[];
    hasMore?: boolean;
    loading?: boolean;
    noSelect?: boolean;
    table: Table<any>;
    totalRecords?: number;
    v1?: boolean;
  }

  const props = withDefaults(defineProps<DataTablePaginationProps>(), {
    fontSize: 'text-sm',
    hasMore: undefined,
  });

  const emits = defineEmits<{
    (e: 'clearSelection'): void;
  }>();

  const hasNextPage = computed<boolean>(() => {
    if (props.hasMore !== undefined) {
      return props.hasMore;
    }

    const { pageIndex, pageSize } = props.table.getState().pagination;
    const numRecords = pageSize * (pageIndex + 1);
    if (props.totalRecords !== undefined) {
      return props.totalRecords > numRecords;
    }

    // When totalRecords is not available, assume there are more pages if we got a full page.
    // This can show an extra empty page if the total is an exact multiple of pageSize.
    // TODO: use N+1 fetch pattern (request pageSize+1, display pageSize) to eliminate this edge case.
    return props.table.getRowCount() >= pageSize;
  });

  const getItemRange = () => {
    if (props.loading) return '';

    const numRows = props.table.getRowModel().rows.length;
    if (numRows === 0) return 'No results';

    const { pageIndex, pageSize } = props.table.getState().pagination;
    const start = pageIndex * pageSize;
    const end = start + Math.min(pageSize, numRows);

    return end === 0 ? '0' : `${start + 1} - ${end}`;
  };
</script>

<template>
  <div class="w-full flex items-center rounded justify-between gap-2 flex-wrap">
    <TableSelection
      v-if="!noSelect"
      :table
      :global-selection
      @clear-selection="emits('clearSelection')"
    >
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </TableSelection>

    <div :class="cn('flex gap-2 items-center', noSelect ? 'ml-auto' : 'lg:ml-auto')">
      <LoadingSpinner v-if="loading" class="mr-2" />

      <div v-else class="rounded px-2 my-auto">
        <span> {{ getItemRange() }} </span>
        <template v-if="totalRecords && !loading">
          of {{ totalRecords.toLocaleString() }}
        </template>
      </div>

      <Separator orientation="vertical" class="h-6" />

      <div class="space-x-2">
        <Button
          variant="ghost"
          size="icon-sm"
          :disabled="!table.getCanPreviousPage()"
          @click="table.setPageIndex(0)"
        >
          <i class="far fa-chevrons-left" />
        </Button>

        <Button
          variant="ghost"
          size="icon-sm"
          :disabled="!table.getCanPreviousPage()"
          @click="table.previousPage()"
        >
          <i class="far fa-chevron-left" />
        </Button>

        <Button variant="ghost" size="icon-sm" :disabled="!hasNextPage" @click="table.nextPage()">
          <i class="far fa-chevron-right" />
        </Button>
      </div>
    </div>
  </div>
</template>
